import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Input } from '@shared/ui/Input';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';

/** Вариант для выпадающего поиска: id + основная подпись + опц. вторичная строка. */
export interface ComboboxOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface Props {
  /** id выбранного варианта ('' — ничего не выбрано). */
  value: string;
  /** Подпись выбранного варианта (показывается «чипом» вместо поля поиска). */
  selectedLabel?: string;
  /** Кандидаты для показа. В серверном режиме — уже отфильтрованные родителем. */
  options: ComboboxOption[];
  onChange: (option: ComboboxOption | null) => void;
  /**
   * Если задан — СЕРВЕРНЫЙ режим: компонент дебаунсит ввод и зовёт `onQueryChange`,
   * а родитель подменяет `options` результатами поиска (масштабируется на любой
   * размер списка). Если НЕ задан — клиентская фильтрация переданных `options`.
   */
  onQueryChange?: (query: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  emptyText?: string;
  debounceMs?: number;
  /** Максимум показываемых строк (защита от рендера тысяч в клиентском режиме). */
  maxItems?: number;
  id?: string;
}

const Wrap = styled.div`
  position: relative;
`;

/** Где рисовать список: под полем (`top`) или над ним (`bottom`), px окна. */
interface Placement {
  top: number | null;
  bottom: number | null;
  left: number;
  width: number;
  maxHeight: number;
}

/** Высота списка по умолчанию и минимум, ниже которого открываем вверх. */
const LIST_MAX_HEIGHT = 300;
const LIST_MIN_ROOM = 200;

/**
 * Список — в портале (`document.body`) с `position: fixed` по прямоугольнику
 * поля. Внутри карточки (`Card` держит свой контекст наложения — анимация с
 * `transform`) абсолютный список с любым z-index уходил ПОД соседние карточки:
 * пункты перекрывались, а клик по ним открывал карточку под списком. Портал не
 * зависит ни от карточек, ни от `overflow` таблиц и модальных окон. Координаты
 * меняются при прокрутке — через `style` (attrs), без генерации классов.
 */
const Results = styled.ul.attrs<{ $place: Placement }>(({ $place }) => ({
  style: {
    top: $place.top ?? undefined,
    bottom: $place.bottom ?? undefined,
    left: $place.left,
    width: $place.width,
    maxHeight: $place.maxHeight,
  },
}))<{ $place: Placement }>`
  position: fixed;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.md};
  list-style: none;
  /* Выше модальных окон: поле выбора бывает и внутри них. */
  z-index: ${({ theme }) => theme.z.modal + 10};
`;

/** Расположение списка у поля: вниз, а если снизу тесно и сверху больше места — вверх. */
function placementFor(field: DOMRect, viewportHeight: number): Placement {
  const below = viewportHeight - field.bottom - 8;
  const above = field.top - 8;
  const up = below < LIST_MIN_ROOM && above > below;
  const room = Math.max(120, Math.min(LIST_MAX_HEIGHT, (up ? above : below) - 4));
  return {
    top: up ? null : field.bottom + 4,
    bottom: up ? viewportHeight - field.top + 4 : null,
    left: field.left,
    width: field.width,
    maxHeight: room,
  };
}

function samePlacement(a: Placement | null, b: Placement): boolean {
  return (
    !!a &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.width === b.width &&
    a.maxHeight === b.maxHeight
  );
}

const ItemLi = styled.li<{ $active?: boolean }>`
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Secondary = styled.div`
  font-size: 11px;
  /* secondary, а не muted: muted мелким текстом не проходит AA. */
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const EmptyLi = styled.li`
  padding: 8px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  list-style: none;
`;

const SelectedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
`;

const ClearBtn = styled.button`
  flex: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
`;

/**
 * Поле с поиском-по-набору (typeahead): печатаешь — фильтрует варианты, выбираешь
 * из выпадающего списка. Заменяет «огромный» `<select>` при большом наборе
 * документов (договоры, сметы). Серверный режим (`onQueryChange`) масштабируется,
 * клиентский — фильтрует переданный список. Навигация ↑/↓/Enter/Esc.
 */
export function Combobox({
  value,
  selectedLabel,
  options,
  onChange,
  onQueryChange,
  placeholder,
  disabled,
  loading,
  emptyText = 'Ничего не найдено',
  debounceMs = 300,
  maxItems = 50,
  id,
}: Props) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const debounced = useDebouncedValue(query, debounceMs);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<Placement | null>(null);

  // Список в портале следует за полем: прокрутка любого контейнера и ресайз окна.
  const reposition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const next = placementFor(el.getBoundingClientRect(), window.innerHeight);
    setPlace((prev) => (samePlacement(prev, next) ? prev : next));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // Серверный режим: пробрасываем дебаунсенный запрос родителю.
  useEffect(() => {
    if (onQueryChange) onQueryChange(debounced.trim());
  }, [debounced, onQueryChange]);

  const q = query.trim().toLowerCase();
  const filtered = (
    onQueryChange
      ? options
      : q
        ? options.filter((o) => `${o.label} ${o.sublabel ?? ''}`.toLowerCase().includes(q))
        : options
  ).slice(0, maxItems);

  // Сброс подсветки при смене набора/запроса.
  useEffect(() => {
    setHighlight(0);
  }, [debounced, options]);

  function pick(option: ComboboxOption) {
    onChange(option);
    setQuery('');
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setQuery('');
    setOpen(true);
  }

  // Выбранное — компактный «чип» с кнопкой сброса (как в CounterpartyPicker:
  // после выбора поле поиска прячем, оно становится визуальным шумом).
  if (value && selectedLabel) {
    return (
      <SelectedRow>
        <span>{selectedLabel}</span>
        {!disabled && (
          <ClearBtn type="button" onClick={clear} aria-label="Сбросить выбор" title="Сбросить">
            ×
          </ClearBtn>
        )}
      </SelectedRow>
    );
  }

  return (
    <Wrap ref={wrapRef}>
      <Input
        id={inputId}
        value={query}
        disabled={disabled}
        autoComplete="off"
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${inputId}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered[highlight] ? `${inputId}-opt-${highlight}` : undefined
        }
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // даём mousedown по элементу успеть раньше, чем blur схлопнет список
          window.setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            if (open && filtered[highlight]) {
              e.preventDefault();
              pick(filtered[highlight]);
            }
          } else if (e.key === 'Escape' && open) {
            // Закрыть список — и только его: боковая панель или окно, внутри
            // которых стоит поле, по этому Esc не закрываются.
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
        }}
      />
      {open &&
        place &&
        createPortal(
          <Results
            id={`${inputId}-listbox`}
            role="listbox"
            $place={place}
            // Нажатие в списке (в том числе на полосу прокрутки) не уводит фокус
            // с поля — иначе blur закрывал список раньше выбора.
            onMouseDown={(e) => e.preventDefault()}
          >
            {filtered.length > 0 ? (
              filtered.map((o, i) => (
                <ItemLi
                  key={o.id}
                  id={`${inputId}-opt-${i}`}
                  role="option"
                  aria-selected={i === highlight}
                  $active={i === highlight}
                  // Выбор — по click, а не mousedown: список закрывается после
                  // отпускания кнопки, и «клик» не достаётся тому, что лежит под
                  // списком (подложке модального окна, карточке-ссылке).
                  onClick={() => pick(o)}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <div>{o.label}</div>
                  {o.sublabel && <Secondary>{o.sublabel}</Secondary>}
                </ItemLi>
              ))
            ) : (
              <EmptyLi>{loading ? 'Поиск…' : emptyText}</EmptyLi>
            )}
          </Results>,
          document.body,
        )}
    </Wrap>
  );
}
